import React, { useEffect, useState } from "react";
import {FormControl, InputLabel, MenuItem, Select, TextField} from '@mui/material';
import { AppDispatch } from "../../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import { editSpaModel } from "../../../redux/actions/spa.actions";
import RootState from "../../../redux/states/root.state";
import { ModalProps } from "../../../components/common/Interfaces/Modals";
import { fetchTubModels } from "../../../redux/actions/tubModels.actions";
import ModalShell, { ModalLoading } from "../../../components/common/Modals/ModalShell";


const EditSpaModelModal: React.FC<ModalProps> = ({open, setOpen}) => {
    const dispatch: AppDispatch = useDispatch();
    const {data:spa, error} = useSelector((state: RootState) => state.spa);
    const {data: tubModels, loading: tubModelsLoading} = useSelector((state: RootState) => state.tubModels);
    const [success, setSuccess] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [name, setName] = useState<any>(null);
    const [sds, setSds] = useState<any>(null);
    const [tier, setTier] = useState<any>(null);
    const [model, setModel] = useState<any>(null);



    useEffect(() => {
        if (spa && open) {
            setModel(spa.tub_model??null);
        }
    }, [spa, open])

    useEffect(() => {
        if (open) {
          const params = new URLSearchParams();

          if (name) params.append('name', name);
          if (sds) params.append('sds', sds.toString());
          if (tier) params.append('tier', tier);

          dispatch(fetchTubModels(params.toString()));
        }
    }, [name, sds, tier, open])

    const handleClose = () => {
      setName(null);
      setSds(null);
      setTier(null);
      setModel(null);
      setSuccess(false);
      setOpen(false);
    };

    const handleEdit = async () => {
        if (!model) {
            alert('Please select a tub model first');
            return;
        }

        setUpdating(true);
        await dispatch(editSpaModel({guid:spa?.id,tub_model_id:model.id}));
        if (!error) {
          setSuccess(true);
        }
        setUpdating(false);
    }

    const tubNameMap: { [key: string]: string } = {
      'Arctic Fox': 'arctic_fox',
      'Summit': 'summit',
      'Summit XL': 'summit_xl',
      'Tundra': 'tundra',
      'Kodiak': 'kodiak',
      'Klondiker': 'klondiker',
      'Yukon': 'yukon',
      'Cub': 'cub',
      'Okanagan': 'okanagan',
      'Ocean': 'ocean',
      'Columbia': 'columbia'
    };

    // Map tiers to folder names
    const tierMap: { [key: string]: string } = {
        'P': model?.sds ? 'prestige-sds' : 'prestige',
        'S': model?.sds ? 'signature-sds' : 'signature',
        'L': model?.sds ? 'legend-sds' : 'legend',
        'LS': model?.sds ? 'legend-select-sds' : 'legend-select'
    };

    const tierMapDisplay: { [key: string]: string } = {
      'P': 'Prestige',
      'S': 'Signature',
      'L': 'Legend',
      'LS': 'Legend Select'
  };

     // Function to determine which image to show based on pump states
     const getSpaImageInfo = () => {
      const tubName = tubNameMap[model?.name];
      const tierName = tierMap[model?.tier];

      // Use a path that should work from the public folder
      // You may need to move the images to public/assets/images/Tubs/ for this to work
      const imagePath = `/assets/images/Tubs/${tubName}/${tierName}/off.png`;

      return imagePath;
  };

  const done = Boolean(error) || success;

  return (
    <ModalShell
      open={open}
      onClose={handleClose}
      size="lg"
      busy={updating}
      tone={updating ? undefined : error ? "error" : success ? "success" : undefined}
      title={updating ? "Updating spa…" : error ? "An error occurred" : success ? "Spa model updated" : "Set spa model"}
      subtitle={!updating && !done ? "Filter the catalogue, then choose the model that matches this spa." : undefined}
      actions={updating ? undefined : done ?
        <button className="btn btn-primary" onClick={handleClose}>Done</button>
        :
        <>
          <button className="btn" onClick={handleClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleEdit} disabled={!model}>Save model</button>
        </>
      }
    >
      {updating ?
        <ModalLoading/>
        : error ?
        <p>{error}</p>
        : success ?
        <p>Spa's model was successfully updated.</p>
        :
        <>
          {/* Filters */}
          <div className="dlg-row">
            <TextField
              onChange={(e:any) =>setName(e.target.value)}
              value={name??``}
              label="Name"
              size="small"
              fullWidth
            />
            <FormControl fullWidth size="small">
              <InputLabel id="tier-selection-label">Tier</InputLabel>
              <Select
                labelId="tier-selection-label"
                label="Tier"
                onChange={(e:any) =>setTier(e.target.value)}
                value={tier??``}
              >
                <MenuItem value={``}>Any tier</MenuItem>
                <MenuItem value={`P`}>P - Prestige</MenuItem>
                <MenuItem value={`S`}>S - Signature</MenuItem>
                <MenuItem value={`L`}>L - Legend</MenuItem>
                <MenuItem value={`LS`}>LS - Legend Select</MenuItem>
              </Select>
            </FormControl>
          </div>
          <div className="segmented" role="group" aria-label="SDS filter" style={{alignSelf: 'flex-start'}}>
            <button type="button" className={!sds ? 'active' : ''} aria-pressed={!sds} onClick={() => setSds(null)}>All</button>
            <button type="button" className={sds ? 'active' : ''} aria-pressed={Boolean(sds)} onClick={() => setSds(true)}>SDS only</button>
          </div>

          {/* Tub Models List */}
          <div className="dlg-section">
            <span className="dlg-label">Models</span>
            {tubModelsLoading ?
              <ModalLoading/>
              : tubModels?.length ?
              <ul className="dlg-options" role="radiogroup" aria-label="Tub models">
                {tubModels.map((tm: any) => (
                  <li key={tm.id}>
                    <label className={`dlg-option ${model?.id === tm.id ? 'selected' : ''}`}>
                      <input type="radio" name="tubModel" value={tm.id} checked={model?.id === tm.id} onChange={() => setModel(tm)}/>
                      <span className="dlg-option-main">{tm.name}</span>
                      <span className="dlg-option-meta">
                        <span>{tierMapDisplay[tm.tier] ?? tm.tier}</span>
                        {Boolean(tm.sds) && <span className="dlg-tag">SDS</span>}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
              :
              <span className="dlg-muted">No models match these filters.</span>
            }
          </div>

          {/* Selected Tub Model Display */}
          {model && (
            <div className="dlg-current" style={{justifyContent: 'flex-start'}}>
              <img
                src={getSpaImageInfo()}
                alt=""
                style={{ width: '72px', aspectRatio: '1/1', objectFit: 'contain', flexShrink: 0 }}
              />
              <div className="dlg-section" style={{gap: '0.15rem'}}>
                <span className="dlg-label">Selected</span>
                <strong style={{textAlign: 'left', textTransform: 'capitalize'}}>
                  {model?.name} {tierMapDisplay[model?.tier]} {model?.sds ? '+ SDS' : ''}
                </strong>
              </div>
            </div>
          )}
        </>
      }
    </ModalShell>
  );
};

export default EditSpaModelModal;
